import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Button,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  Divider,
  Grid,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { useAuthState, useDataProvider, useNotify } from 'react-admin';
import { PageTitle } from '../../components/PageTitle';
import PublicIcon from '@mui/icons-material/Public';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';

interface BrandPortalConfig {
  id: string;
  brandId: string;
  portalWelcomeMessage?: string;
  portalRequestConfirmationMessage?: string;
  portalDefaultWorkflowId?: string;
  portalDefaultStatusId?: string;
}

export const BrandPortalConfiguration = () => {
  const { identity } = useAuthState();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const navigate = useNavigate();

  const [selectedBrandId, setSelectedBrandId] = useState<string>('');
  const [brands, setBrands] = useState<any[]>([]);
  const [config, setConfig] = useState<BrandPortalConfig | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [workflows, setWorkflows] = useState<any[]>([]);
  const [statuses, setStatuses] = useState<any[]>([]);

  // Also check localStorage as fallback
  const storedUser = React.useMemo(() => {
    try {
      const stored = localStorage.getItem('user');
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      return null;
    }
  }, []);

  const isEnterpriseAdmin =
    (identity as any)?.isEnterpriseAdmin === true ||
    storedUser?.isEnterpriseAdmin === true;

  // Fetch brands when component mounts
  React.useEffect(() => {
    const fetchBrands = async () => {
      try {
        const response = await dataProvider.getList('brands', {
          pagination: { page: 1, perPage: 100 },
          sort: { field: 'name', order: 'ASC' },
          filter: {},
        });
        setBrands(response.data);
      } catch (error) {
        console.error('Error fetching brands:', error);
        notify('Failed to load brands', { type: 'error' });
      }
    };

    if (isEnterpriseAdmin) {
      fetchBrands();
    }
  }, [isEnterpriseAdmin, dataProvider, notify]);

  // Fetch config when brand is selected
  React.useEffect(() => {
    if (selectedBrandId) {
      const fetchConfig = async () => {
        setLoading(true);
        try {
          // Get brand with portal settings
          const response = await dataProvider.getOne('brands', {
            id: selectedBrandId,
          });

          setConfig({
            id: response.data.id,
            brandId: response.data.id,
            portalWelcomeMessage: response.data.portalWelcomeMessage,
            portalRequestConfirmationMessage: response.data.portalRequestConfirmationMessage,
            portalDefaultWorkflowId: response.data.portalDefaultWorkflowId,
            portalDefaultStatusId: response.data.portalDefaultStatusId,
          });

          // Fetch workflows for this brand
          const workflowsResponse = await dataProvider.getList('workflows', {
            pagination: { page: 1, perPage: 100 },
            sort: { field: 'name', order: 'ASC' },
            filter: { brandId: selectedBrandId },
          });
          setWorkflows(workflowsResponse.data);
        } catch (error) {
          console.error('Error fetching config:', error);
          notify('Failed to load portal configuration', { type: 'error' });
        } finally {
          setLoading(false);
        }
      };

      fetchConfig();
    }
  }, [selectedBrandId, dataProvider, notify]);

  const handleSave = async () => {
    if (!config || !selectedBrandId) return;

    setSaving(true);
    try {
      // Update brand with new portal settings
      await dataProvider.update('brands', {
        id: selectedBrandId,
        data: {
          portalWelcomeMessage: config.portalWelcomeMessage,
          portalRequestConfirmationMessage: config.portalRequestConfirmationMessage,
          portalDefaultWorkflowId: config.portalDefaultWorkflowId,
          portalDefaultStatusId: config.portalDefaultStatusId,
        },
      });
      notify('Portal configuration saved successfully', { type: 'success' });
    } catch (error) {
      console.error('Error saving config:', error);
      notify('Failed to save portal configuration', { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (!isEnterpriseAdmin) {
    return (
      <Box>
        <PageTitle
          title="Brand Customer Portal"
          subtitle="Configure customer portal for brands"
          icon={<PublicIcon />}
        />
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography color="warning">
              Only enterprise administrators can manage brand portal settings
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/configuration/enterprise-admin')}
          variant="outlined"
        >
          Back
        </Button>
        <Box>
          <PageTitle
            title="Brand Customer Portal"
            subtitle="Configure customer portal settings for brands"
            icon={<PublicIcon />}
          />
        </Box>
      </Box>

      {/* Brand Selector */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <TextField
            select
            label="Select Brand"
            value={selectedBrandId}
            onChange={(e) => {
              setSelectedBrandId(e.target.value);
              setConfig(null);
              setWorkflows([]);
            }}
            sx={{ minWidth: 300 }}
            disabled={brands.length === 0}
            fullWidth
          >
            <MenuItem value="">-- Choose a brand --</MenuItem>
            {brands.map((brand) => (
              <MenuItem key={brand.id} value={brand.id}>
                {brand.name}
              </MenuItem>
            ))}
          </TextField>
        </CardContent>
      </Card>

      {/* Portal Configuration */}
      {selectedBrandId && (
        <>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={3}>
              {/* Welcome Message */}
              <Grid item xs={12}>
                <Card>
                  <CardHeader
                    title="Welcome Message"
                    subheader="Message shown when customers first access the portal"
                  />
                  <CardContent>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      label="Welcome Message"
                      value={config?.portalWelcomeMessage || ''}
                      onChange={(e) =>
                        setConfig({
                          ...config!,
                          portalWelcomeMessage: e.target.value,
                        })
                      }
                      placeholder="Welcome to our customer portal..."
                    />
                  </CardContent>
                </Card>
              </Grid>

              {/* Request Confirmation Message */}
              <Grid item xs={12}>
                <Card>
                  <CardHeader
                    title="Request Confirmation Message"
                    subheader="Message shown after customer submits a request"
                  />
                  <CardContent>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      label="Confirmation Message"
                      value={config?.portalRequestConfirmationMessage || ''}
                      onChange={(e) =>
                        setConfig({
                          ...config!,
                          portalRequestConfirmationMessage: e.target.value,
                        })
                      }
                      placeholder="Your request has been received..."
                    />
                  </CardContent>
                </Card>
              </Grid>

              {/* Default Workflow */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardHeader title="Default Workflow" subheader="Default workflow for new requests" />
                  <CardContent>
                    <TextField
                      select
                      fullWidth
                      label="Workflow"
                      value={config?.portalDefaultWorkflowId || ''}
                      onChange={(e) =>
                        setConfig({
                          ...config!,
                          portalDefaultWorkflowId: e.target.value,
                        })
                      }
                    >
                      <MenuItem value="">-- None --</MenuItem>
                      {workflows.map((workflow) => (
                        <MenuItem key={workflow.id} value={workflow.id}>
                          {workflow.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </CardContent>
                </Card>
              </Grid>

              {/* Default Status */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardHeader title="Default Status" subheader="Initial status for new requests" />
                  <CardContent>
                    <TextField
                      fullWidth
                      label="Default Status ID"
                      value={config?.portalDefaultStatusId || ''}
                      onChange={(e) =>
                        setConfig({
                          ...config!,
                          portalDefaultStatusId: e.target.value,
                        })
                      }
                      placeholder="Enter status ID"
                    />
                    <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                      Leave empty to use first status from workflow
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* Save Button */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save Configuration'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          )}
        </>
      )}
    </Box>
  );
};
